import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { StudioFrame } from "@/components/site/StudioFrame";
import { PRODUCTS } from "@/lib/site-data";

const ProductCard = ({ p }) => (
  <motion.div
    whileHover={{ y: -8 }}
    transition={{ type: "spring", stiffness: 260, damping: 24 }}
    data-testid={`product-card-${p.id}`}
    className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-avatar-ink/10 bg-white p-8 shadow-[0_24px_70px_rgba(21,21,23,0.07)]"
  >
    <div
      className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full opacity-0 transition-opacity duration-500 group-hover:opacity-100"
      style={{ background: `radial-gradient(circle, ${p.band}33, transparent 60%)` }}
    />

    <div className="relative z-10 flex items-start justify-between">
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-avatar-ink/40">
          {p.layers}-Layer
        </span>
        <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight text-avatar-ink md:text-3xl">
          {p.name}
        </h3>
        <p className="mt-1 text-sm font-medium" style={{ color: p.band === "#071C3F" ? "#8ea6c9" : p.band }}>
          {p.tagline}
        </p>
      </div>
      <span
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-heading text-lg font-bold text-avatar-ink"
        style={{ background: p.band }}
      >
        {p.layers}
      </span>
    </div>

    <div className="relative z-10 my-6">
      <StudioFrame
        src={p.image}
        alt={`${p.name} water tank`}
        glow={p.band}
        blend={!p.poster}
        className="mx-auto max-w-[300px]"
        imgClassName={p.poster ? "" : "p-4"}
        rounded="rounded-2xl"
      />
    </div>

    <p className="relative z-10 text-sm leading-relaxed text-avatar-ink/55">{p.description}</p>

    <ul className="relative z-10 mt-6 grid grid-cols-2 gap-3">
      {p.features.map((f) => (
        <li key={f} className="flex items-center gap-2 text-xs font-medium text-avatar-ink/70">
          <Check size={13} style={{ color: p.band === "#071C3F" ? "#8ea6c9" : p.band }} />
          {f}
        </li>
      ))}
    </ul>
  </motion.div>
);

export const Products = () => (
  <section id="products" className="relative mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-32">
    <Reveal className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <div>
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-avatar-red">
          (The Range)
        </p>
        <h2 className="max-w-2xl font-heading text-4xl font-bold leading-none tracking-tighter text-avatar-ink md:text-6xl">
          Four grades.<br />One standard of purity.
        </h2>
      </div>
      <p className="max-w-sm text-sm leading-relaxed text-avatar-ink/55">
        From essential 3-layer protection to flagship 5-layer Self Clean
        technology — every Avatar tank is engineered to last.
      </p>
    </Reveal>

    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {PRODUCTS.map((p, i) => (
        <Reveal key={p.id} delay={i * 0.08}>
          <ProductCard p={p} />
        </Reveal>
      ))}
    </div>
  </section>
);
